import React, { ComponentPropsWithoutRef } from "react";
import cn from "../util/cn";

type TextInputProps = ComponentPropsWithoutRef<"input">;

export const TextInput = React.forwardRef<HTMLInputElement, TextInputProps>(
  ({ className, ...otherProps }: TextInputProps, ref) => (
    <input
      className={cn(
        "block w-full rounded-md border-0 px-1.5 py-1.5 text-sm leading-6 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 disabled:text-gray-400",
        className
      )}
      type="text"
      ref={ref}
      {...otherProps}
    />
  )
);

type NumberInputProps = {
  step?: number;
  isError?: boolean;
} & Omit<ComponentPropsWithoutRef<"input">, "type" | "step">;

export const NumberInput = React.forwardRef<HTMLInputElement, NumberInputProps>(
  ({ step, className, isError, ...otherProps }: NumberInputProps, ref) => (
    <input
      type="number"
      inputMode="numeric"
      className={cn(
        "m-0 block min-w-0 appearance-none rounded-md border-0 p-0 leading-6 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 disabled:text-gray-400",
        isError ? "ring-red-600 focus:ring-red-600" : "",
        className
      )}
      step={step}
      ref={ref}
      {...otherProps}
    />
  )
);

type EmailInputProps = Omit<ComponentPropsWithoutRef<"input">, "autoComplete"> & {
  autoComplete?: "email" | "username";
};

export const EmailInput = React.forwardRef<HTMLInputElement, EmailInputProps>(
  ({ autoComplete = "email", className, ...otherProps }: EmailInputProps, ref) => (
    <input
      className={cn(
        "block w-full rounded-md border-0 px-1.5 py-1.5 text-sm leading-6 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 disabled:text-gray-400",
        className
      )}
      type="email"
      autoComplete={autoComplete}
      ref={ref}
      {...otherProps}
    />
  )
);

type PasswordInputProps = {
  autoComplete?: "current-password" | "new-password";
} & Omit<ComponentPropsWithoutRef<"input">, "autoComplete">;

export const PasswordInput = React.forwardRef<HTMLInputElement, PasswordInputProps>(
  ({ className, ...otherProps }: PasswordInputProps, ref) => (
    <input
      className={cn(
        "block w-full rounded-md border-0 px-1.5 py-1.5 text-sm leading-6 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 disabled:text-gray-400",
        className
      )}
      type="password"
      ref={ref}
      {...otherProps}
    />
  )
);

type SelectInputProps<T extends string> = {
  onChange: (value: T) => void;
  options: ({ value: T; title: string } & Omit<
    ComponentPropsWithoutRef<"option">,
    "key" | "value" | "children"
  >)[];
  className?: string;
} & Omit<ComponentPropsWithoutRef<"select">, "onChange" | "children">;

// TODO: Surround with forwardRef
export function SelectInput<T extends string>({
  onChange,
  options,
  className,
  ...otherProps
}: SelectInputProps<T>) {
  return (
    <select
      className={cn(
        "block w-full rounded-md border-0 pb-1.5 pl-1.5 pr-10 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-blue-600 disabled:text-gray-400",
        className
      )}
      onChange={(e) => onChange(e.target.value as T)}
      {...otherProps}
    >
      {options.map(({ value, title, ...otherProps }, i) => (
        <option key={`option-${i}`} value={value} {...otherProps}>
          {title}
        </option>
      ))}
    </select>
  );
}

type DateInputProps = ComponentPropsWithoutRef<"input">;

export const DateInput = React.forwardRef<HTMLInputElement, DateInputProps>(
  ({ className, ...otherProps }: DateInputProps, ref) => (
    <input
      className={cn(
        "block w-full rounded-md border-0 px-1.5 py-1.5 text-sm leading-6 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 disabled:text-gray-400",
        className
      )}
      type="date"
      ref={ref}
      {...otherProps}
    />
  )
);

export const RadioInput = (props: Omit<ComponentPropsWithoutRef<"input">, "type">) => (
  <input type="radio" {...props} />
);
